/**
 * Shared model-display badges: a vendor logo (ProviderBadge), a serving-cloud
 * chip (CloudProviderBadge, FRONT-1655) and a hosting region flag
 * (RegionBadge). Used by the settings model picker and the chat composer's
 * image-model picker (FRONT-535).
 *
 * "Vendor" (deriveFamily/ProviderBadge) and "provider" (deriveCloudProvider/
 * CloudProviderBadge) are different axes — see CONTEXT.md's glossary entry.
 * Vendor = who makes the model (Anthropic, OpenAI, ...); provider = who
 * serves it (Bedrock, Azure, Google, StackIt, or the vendor's own
 * direct API).
 *
 * Logos are lobe-icons ported from the platform (public/model-logos/{light,dark})
 * so Eddy matches the platform's look; region flags are webp (public/region-flags).
 * The vendor is derived from the model alias (the proxy's `provider` reports the
 * serving credential, which is unreliable for grouping), and the logo is chosen
 * by theme. Brand colours/logos are a deliberate exception to the
 * one-green-one-red rule (vendor/provider identity, not app state).
 */

import type { ReactElement } from 'react';
import { useTheme } from '~/lib/stores/theme-store';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';

export type Region = 'eu' | 'us' | 'cn' | 'de' | 'global' | 'multi';

const PROVIDER_FALLBACK_COLOR = 'bg-muted/40 text-foreground/70 border-border';

interface FamilyMeta {
  label: string;
  glyph: string;
  color: string;
  /** Developer logo assets (themed). Absent → fall back to the glyph badge. */
  logo?: { light: string; dark: string };
}

/** Build a light/dark asset pair for a logo filename under public/model-logos. */
function logoPair(file: string): { light: string; dark: string } {
  return { light: `/model-logos/light/${file}`, dark: `/model-logos/dark/${file}` };
}

const GOOGLE_META: FamilyMeta = { label: 'Google', glyph: 'GO', color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30', logo: logoPair('gemini-color.svg') };

const FAMILY_RULES: Array<{ test: RegExp; meta: FamilyMeta }> = [
  { test: /claude/, meta: { label: 'Anthropic', glyph: 'AN', color: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30', logo: logoPair('claude-color.svg') } },
  { test: /(^|[-/])(gpt|o[1-4]|codex)([-./]|$)|openai/, meta: { label: 'OpenAI', glyph: 'AI', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', logo: logoPair('openai.svg') } },
  { test: /gemini|gemma|palm|bison|imagen/, meta: GOOGLE_META },
  { test: /flux/, meta: { label: 'Black Forest Labs', glyph: 'BF', color: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border-zinc-500/30', logo: logoPair('flux.svg') } },
  { test: /mistral|magistral|mixtral|codestral|ministral|pixtral|devstral/, meta: { label: 'Mistral', glyph: 'MI', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30', logo: logoPair('mistral-color.svg') } },
  { test: /grok/, meta: { label: 'xAI', glyph: 'XA', color: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border-zinc-500/30', logo: logoPair('grok.svg') } },
  { test: /llama/, meta: { label: 'Meta', glyph: 'ME', color: 'bg-blue-600/15 text-blue-700 dark:text-blue-300 border-blue-600/30', logo: logoPair('meta-color.svg') } },
  { test: /deepseek/, meta: { label: 'DeepSeek', glyph: 'DS', color: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30', logo: logoPair('deepseek-color.svg') } },
  { test: /qwen/, meta: { label: 'Alibaba', glyph: 'QW', color: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30' } },
  { test: /command|cohere/, meta: { label: 'Cohere', glyph: 'CO', color: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' } },
  { test: /(^|[-/])nova|titan/, meta: { label: 'Amazon', glyph: 'AM', color: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30' } },
  { test: /kimi|moonshot/, meta: { label: 'Moonshot', glyph: 'KI', color: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30' } },
  { test: /(^|[-/])glm|zhipu/, meta: { label: 'Zhipu', glyph: 'GL', color: 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30' } },
];

// Generic cloud-hosting-platform token — checked only as deriveFamily's LAST
// resort (see below), never inside FAMILY_RULES itself. Vertex/Google is the
// only cloud whose bare name doubles as a fallback vendor label today.
const GENERIC_CLOUD_HOST_TOKEN = /(^|[-/])vertex|(^|[-/])google/;

const FAMILY_FALLBACK: FamilyMeta = { label: 'Other', glyph: '··', color: PROVIDER_FALLBACK_COLOR };

/**
 * Map a LiteLLM alias to its display vendor. Matches FAMILY_RULES against
 * the alias with its cloud/deployment wrapper stripped (`coreName`, shared
 * with `compareModelsNewestFirst`'s tie-break below) rather than the raw
 * alias — first rule wins on the STRIPPED core. FRONT-1633: this is what
 * makes FAMILY_RULES' order safe to append to. Testing the raw alias let a
 * generic cloud-hosting token (`google-`, `vertex-`) outrank a real vendor
 * keyword whenever the cloud rule happened to sit above it in the list
 * (e.g. `google-vertex-mistral-large` matching "Google" before "Mistral"
 * ever got a turn); stripping the cloud wrapper first means only the
 * vendor's OWN keyword can match, so a future rule can be added anywhere
 * in FAMILY_RULES without re-litigating ordering against every existing one.
 *
 * If nothing in FAMILY_RULES recognizes the real vendor even after
 * stripping, the cloud-hosting platform itself becomes the fallback label
 * (an unrecognized model served via Vertex still reads as "Google" rather
 * than "Other") — checked ONLY here as a last resort, never as a rule
 * inside FAMILY_RULES.
 */
export function deriveFamily(id: string): FamilyMeta {
  const v = id.toLowerCase();
  const core = coreName(v);
  for (const f of FAMILY_RULES) if (f.test.test(core)) return f.meta;
  if (GENERIC_CLOUD_HOST_TOKEN.test(v)) return GOOGLE_META;
  return FAMILY_FALLBACK;
}

// Tokens that look numeric but are NOT a version: hyphen-joined datestamps,
// plain datestamps, parameter/expert counts, context sizes. Dropped before
// picking the version, so none of them can masquerade as (or get welded
// into) a real version number (FRONT-1634).
//
// A hyphen-joined, YEAR-FIRST datestamp ("2024-11-20") MUST be stripped
// before `foldHyphenVersion` runs, or folding welds it into a decimal
// (2024-11 -> 2024.11) that then evades the plain DATESTAMP_TOKEN below,
// since that regex excludes runs already adjacent to a dot. Real example:
// `gpt-image-2-2026-04-21` (src/llm/genai-image.ts) is the SAME model as
// bare `gpt-image-2` and must read as the same version (2), not 2026.x.
// Deliberately NOT matching a leading "MM-" before the year (a MONTH-first
// date like "08-2024") — that would risk eating a genuine preceding version
// digit (the "2" in gpt-image-2-2026-...) into the same match. A month-first
// date's leftover 2-digit fragment (the "08") is instead caught by
// AMBIGUOUS_BARE_TWO_DIGIT_TOKEN below, landing on the same "versionless"
// outcome without that risk.
const HYPHEN_DATESTAMP_TOKEN = /(?<![\d.])\d{4}(?:-\d{1,2}){0,2}(?![\d.])/g;
const DATESTAMP_TOKEN = /(?<![\d.])\d{4,}(?![\d.])/g;
// "NxMb"/"NxMe" mixture-of-experts shape (8x7b, 8x22b — real Mixtral naming,
// already recognized by FAMILY_RULES below) MUST be stripped as one token
// before PARAM_OR_EXPERT_COUNT_TOKEN below, or that regex only eats the
// trailing "7b"/"22b" half and leaves the leading expert-count digit ("8")
// behind to be picked up as a fake version.
const MOE_COUNT_TOKEN = /\d+x\d+(?:\.\d+)?[be](?=[-_.]|$)/g;
// Parameter counts (235b, 80b) and mixture-of-experts counts (16e, 128e —
// real Llama 4 Scout/Maverick naming) share the same "count+letter" shape.
const PARAM_OR_EXPERT_COUNT_TOKEN = /\d+(?:\.\d+)?[be](?=[-_.]|$)/g;
const CONTEXT_SIZE_TOKEN = /\d+k(?=[-_.]|$)/g;
// A zero-padded 3-digit revision suffix ("-002" in gemini-1.5-pro-002, a
// real Vertex AI naming convention) is a revision counter, not a version —
// the leading zero is what distinguishes it from a genuine 3-digit version.
const REVISION_SUFFIX_TOKEN = /(?<=-)0\d{2}(?=-|$)/g;
// A bare 2-digit token with no separator (e.g. "56" in azure-ai-gpt-56-terra,
// or the Azure "gpt-35-turbo" family) is ambiguous — could be version 56 (or
// 35), or a squashed "5.6" (or "3.5") — and nothing in the real offering
// resolves it, so it's dropped and the id reads as unknown/versionless (0)
// rather than guessed. Flagged in the FRONT-1634 PR body for a call on
// whether to fold it instead.
const AMBIGUOUS_BARE_TWO_DIGIT_TOKEN = /(?<=-)\d{2}(?=-|$)/g;

/**
 * Fold a hyphen-split version ("opus-4-7") into its dotted form ("opus-4.7")
 * BEFORE final token extraction, so it reads as one version token instead of
 * two unrelated integers. Only fires when the second number is itself a
 * whole token (immediately followed by a separator or the end of the id) —
 * that keeps it from firing inside e.g. a parameter-count suffix. Must run
 * AFTER the datestamp strips above, or it would weld a real date into a
 * decimal first.
 */
function foldHyphenVersion(id: string): string {
  return id.replace(/(\d+)-(\d+)(?=[-_.]|$)/g, '$1.$2');
}

function modelVersion(id: string): number {
  let stripped = id.toLowerCase();
  stripped = stripped.replace(HYPHEN_DATESTAMP_TOKEN, '');
  stripped = stripped.replace(DATESTAMP_TOKEN, '');
  stripped = foldHyphenVersion(stripped);
  stripped = stripped.replace(MOE_COUNT_TOKEN, '');
  stripped = stripped.replace(PARAM_OR_EXPERT_COUNT_TOKEN, '');
  stripped = stripped.replace(CONTEXT_SIZE_TOKEN, '');
  stripped = stripped.replace(REVISION_SUFFIX_TOKEN, '');
  stripped = stripped.replace(AMBIGUOUS_BARE_TWO_DIGIT_TOKEN, '');
  const nums = stripped.match(/\d+(?:\.\d+)?/g);
  return nums ? Math.max(...nums.map(Number)) : 0;
}

// Cloud/deployment tokens that vary per hosting option for the SAME model —
// stripping them before the tie-break groups e.g. bedrock-opus-5 next to
// google-opus-5 instead of splitting the two clouds' opus/sonnet rows apart.
// ORDER IS LOAD-BEARING: `.replace` on a plain (non-global) regex only
// removes the first match, so a more specific prefix must precede its more
// general overlap — google-vertex- before google-, azure-ai- before azure- —
// or the specific one never gets a turn.
const CLOUD_PREFIXES = [/^bedrock-/, /^google-vertex-/, /^google-/, /^azure-ai-/, /^azure-/];
// Shared so `coreName`'s stripping, `deriveCloudProvider`'s provider-identity
// match, and `deriveRegion`'s Germany override can't silently drift apart —
// a future StackIt naming-convention change is then one regex, not three.
const STACKIT_SUFFIX_RE = /-stackit$/;
const DEPLOYMENT_SUFFIXES = [/-maas$/, STACKIT_SUFFIX_RE];

/**
 * Same-model-different-cloud key: an alias with its cloud/deployment wrapper
 * stripped. Exported for FRONT-1655's model-first grouping (`coreName(id)` is
 * the group key for "which rows are the same model"), in addition to its
 * original use as `compareModelsNewestFirst`'s tie-break.
 */
export function coreName(id: string): string {
  let core = id.toLowerCase();
  for (const prefix of CLOUD_PREFIXES) core = core.replace(prefix, '');
  for (const suffix of DEPLOYMENT_SUFFIXES) core = core.replace(suffix, '');
  return core;
}

export interface CloudProviderMeta {
  /** Stable key for React list keys / lookups — never shown to the user. */
  key: string;
  label: string;
  logo?: { light: string; dark: string };
  /** Fallback glyph + color when there's no logo (mirrors FamilyMeta). */
  glyph?: string;
  color?: string;
}

/**
 * FRONT-1655: the SERVING CLOUD/HOST — Bedrock, Azure, Google,
 * StackIt, or a vendor's own direct API — as distinct from `deriveFamily`'s
 * VENDOR (Anthropic, OpenAI, ...). Two different models from two different
 * vendors can share a provider (Bedrock hosts both Claude and Llama); one
 * model can be offered by several providers (Claude Opus 4.6 direct AND via
 * Bedrock; Mistral Large direct AND via Vertex — verified in
 * blockbrain-workloads/infra/litellm). See CONTEXT.md's glossary entry.
 *
 * Order mirrors `CLOUD_PREFIXES`: a more specific prefix (`google-vertex-`)
 * must be tested before its more general overlap (`google-`) or the
 * specific one never gets a turn — same load-bearing ordering rule as
 * `coreName`.
 */
const CLOUD_PROVIDER_RULES: Array<{ test: RegExp; meta: CloudProviderMeta }> = [
  { test: /^bedrock-/, meta: { key: 'bedrock', label: 'AWS Bedrock', logo: logoPair('bedrock-color.svg') } },
  { test: /^google-vertex-/, meta: { key: 'vertex', label: 'Google', logo: logoPair('google-color.svg') } },
  { test: /^google-/, meta: { key: 'vertex', label: 'Google', logo: logoPair('google-color.svg') } },
  { test: /^azure-ai-/, meta: { key: 'azure', label: 'Azure', logo: logoPair('azureai-color.svg') } },
  { test: /^azure-/, meta: { key: 'azure', label: 'Azure', logo: logoPair('azureai-color.svg') } },
  // `-maas` is Vertex's "Model as a Service" naming. It USUALLY pairs with
  // a google-/google-vertex- prefix matched above, but a bare
  // `llama-4-maverick-17b-128e-instruct-maas` exists in this repo's own
  // fixtures, and coreName strips the suffix unconditionally — so without
  // this rule such an alias would wear a confident, wrong "<Vendor> API"
  // chip on the same row as its correctly-labelled twin.
  { test: /-maas$/, meta: { key: 'vertex', label: 'Google', logo: logoPair('google-color.svg') } },
  { test: STACKIT_SUFFIX_RE, meta: { key: 'stackit', label: 'StackIt', logo: logoPair('stackit.svg') } },
];

/**
 * No recognized cloud prefix/suffix → the vendor's own direct API. The chip
 * wears the VENDOR's logo (Mistral serving Mistral, Anthropic serving
 * Anthropic) — "served by the maker itself" is exactly what the chip means
 * here, and a text glyph ("DI") next to real cloud logos read as an
 * unexplained abbreviation (2026-08-30 review). `key` stays 'direct' so
 * grouping/redundancy logic sees one provider regardless of vendor.
 */
function directProviderMeta(id: string): CloudProviderMeta {
  const family = deriveFamily(id);
  const named = family.label !== 'Other';
  return {
    key: 'direct',
    label: named ? `${family.label} API` : 'Direct API',
    logo: family.logo,
    glyph: named ? family.glyph : 'API',
    color: named ? family.color : PROVIDER_FALLBACK_COLOR,
  };
}

/**
 * Which serving cloud/host an alias resolves to. Pure keyword match against
 * the raw (unstripped) alias — order matters, see `CLOUD_PROVIDER_RULES`.
 * Falls back to "Direct API" so an alias with no cloud wrapper (the vendor's
 * own endpoint, e.g. `anthropic-claude-opus-4.6`, `mistral-large`) still
 * gets a real, informative provider identity rather than "Other".
 */
export function deriveCloudProvider(id: string): CloudProviderMeta {
  const v = id.toLowerCase();
  for (const r of CLOUD_PROVIDER_RULES) if (r.test.test(v)) return r.meta;
  return directProviderMeta(id);
}

/**
 * FRONT-1979: StackIt's whole catalogue is served from one Germany-South
 * datacenter — always, invariantly — so "this row is Germany" is a display
 * fact we can state outright, the same way `deriveCloudProvider` above
 * already reads a `-stackit` alias suffix rather than a proxy-reported
 * field. The backend's own Region model deliberately stays free of this
 * kind of name-derived inference (see the header comment in
 * `src/llm/model-catalogue.ts`), so the override lives here, at display
 * time, and never touches what the server reports as `region`.
 */
export function deriveRegion(id: string, region: Region | undefined): Region | undefined {
  return STACKIT_SUFFIX_RE.test(id.toLowerCase()) ? 'de' : region;
}

/**
 * cmdk `filter` shared by every model picker: tokenize the query on
 * whitespace and require EVERY word to be a substring of the row's
 * `modelSearchText`, so "opus 4.8" matches "claude-opus-4.8" and "eu
 * mistral" narrows to EU-served Mistral rows. Deliberately not cmdk's
 * default fuzzy scorer — a subsequence match would let "eu" hit
 * "cl(e)a(u)de" and make the region/provider words in the haystack
 * meaningless. Returns 1/0 (match/no match); ordering stays the
 * catalogue's own.
 */
export function modelSearchFilter(itemValue: string, search: string): number {
  const hay = itemValue.toLowerCase();
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  return words.every((w) => hay.includes(w)) ? 1 : 0;
}

/**
 * FRONT-1655: one deduped picker row — every proxy alias that is the SAME
 * model (`coreName` matches) served by one or more providers (clouds).
 * `entries` is pre-sorted by `regionWeight` (EU first); `primary` is
 * `entries[0]`. Which entry a plain row click actually saves is
 * `rowClickTarget`, not `primary` — see there.
 */
export interface ModelRow<T extends PickerModel = PickerModel> {
  coreKey: string;
  entries: T[];
  primary: T;
}

/**
 * FRONT-1655: the two-level grouping shared by `ModelPicker`
 * (models-section.tsx) and `ModelCombobox` (custom-agent-form.tsx) — the
 * design doc asked for one implementation, and the first pass shipped two
 * byte-identical copies that had already started to diverge. Vendor
 * (`deriveFamily` — NOT the proxy's owned_by, which reports the serving
 * credential: Gemini lands under "openai", o3 under "gradient_ai") is the
 * heading level; `coreName` collapses cloud twins into one row; rows are
 * newest-first (FRONT-250/1634) by their primary alias. Display only —
 * routing always uses the exact alias id the user picked.
 */
export function groupModelRows<T extends PickerModel>(models: readonly T[]): Record<string, ModelRow<T>[]> {
  const byVendor: Record<string, Map<string, T[]>> = {};
  for (const m of models) {
    const vendorKey = deriveFamily(m.id).label;
    const core = coreName(m.id);
    const byCore = (byVendor[vendorKey] ??= new Map());
    (byCore.get(core) ?? byCore.set(core, []).get(core)!).push(m);
  }
  const out: Record<string, ModelRow<T>[]> = {};
  for (const [vendorKey, byCore] of Object.entries(byVendor)) {
    const rows: ModelRow<T>[] = Array.from(byCore.entries()).map(([coreKey, entries]) => {
      // No per-model cost/latency data reaches the frontend, so a
      // same-region tie keeps the catalogue's own order rather than
      // fabricating a second ranking signal.
      const sorted = [...entries].sort(
        (a, b) => regionWeight(deriveRegion(a.id, a.region)) - regionWeight(deriveRegion(b.id, b.region)),
      );
      return { coreKey, entries: sorted, primary: sorted[0] };
    });
    rows.sort((a, b) => compareModelsNewestFirst(a.primary.id, b.primary.id));
    out[vendorKey] = rows;
  }
  return out;
}

/** Vendor heading order: Anthropic first (the role defaults live there), then alphabetical, "Other" last. */
export function sortVendorGroups(keys: readonly string[]): string[] {
  const rank = (k: string) => (k === 'Anthropic' ? 0 : k === 'Other' ? 2 : 1);
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * FRONT-1655: the alias a plain ROW click saves — and therefore the chip
 * that carries the "used when you click the row" mark. If the deployment/
 * org default sits on this row under any provider, the row click means
 * "use the default" and lands there; otherwise the EU-first primary. One
 * function for both the click handler and the chip flag, because the
 * first pass drove them from two different rules (the click used the
 * default, the mark used the primary) and pointed the affordance at the
 * wrong provider whenever the two differed.
 */
export function rowClickTarget<T extends PickerModel>(row: ModelRow<T>, defaultValue?: string): T {
  return row.entries.find((e) => e.id === defaultValue) ?? row.primary;
}

/**
 * FRONT-250/FRONT-1634: newest-first ordering inside a vendor group.
 * Compares by the model's actual version token — parameter counts, context
 * sizes and datestamps are dropped first so they can never masquerade as a
 * version (see `modelVersion`). Ties break on the "core name" — the alias
 * with its cloud/deployment prefix and suffix stripped — so e.g.
 * bedrock-opus-5 and google-opus-5 land adjacent instead of splitting apart
 * by cloud prefix; a final full-alias compare keeps that stable.
 *
 * Since FRONT-1655 the custom-agent model picker (`custom-agent-form.tsx`)
 * goes through `groupModelRows` below and therefore through this comparator
 * too; the server-side `byProvider` grouping it used to render is no longer
 * consumed by any picker (still sent on the wire — its removal is a
 * backend follow-up, see the design doc's non-goals).
 */
export function compareModelsNewestFirst(a: string, b: string): number {
  const d = modelVersion(b) - modelVersion(a);
  if (d !== 0) return d;
  const coreD = coreName(a).localeCompare(coreName(b));
  return coreD !== 0 ? coreD : a.localeCompare(b);
}

const REGION_LABEL: Record<Region, string> = {
  eu: 'EU',
  us: 'US',
  cn: 'CN',
  de: 'DE',
  global: 'Global',
  multi: 'Multi',
};

// `multi` and `global` have no flag (mixed deployment / no fixed residency),
// so they keep a neutral text chip; eu/us/cn/de render the ported flag webp.
const REGION_CHIP_COLOR: Record<Region, string> = {
  eu: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30',
  de: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30',
  us: 'bg-muted text-muted-foreground border border-border',
  cn: 'bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30',
  global: 'bg-muted text-muted-foreground border border-border',
  multi: 'bg-muted text-muted-foreground border border-border',
};

export function ProviderBadge({ id }: { id: string }) {
  const family = deriveFamily(id);
  const { theme } = useTheme();
  if (family.logo) {
    const src = theme === 'dark' ? family.logo.dark : family.logo.light;
    return (
      <Tip label={family.label}>
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded border border-border bg-background"
        >
          <img src={src} alt={family.label} className="w-4 h-4 object-contain" />
        </span>
      </Tip>
    );
  }
  return (
    <Tip label={family.label}>
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded text-[length:var(--text-2xs)] font-semibold border ${family.color}`}
      >
        {family.glyph}
      </span>
    </Tip>
  );
}

/**
 * Unknown region → assume US. Most models on the proxy route to US-hosted
 * vendor APIs unless explicitly tagged otherwise, so an empty chip would be
 * misleading.
 */
function effectiveRegion(region?: Region): Region {
  return region ?? 'us';
}

/**
 * Stable ordering: EU/DE > global > US (or unknown) > multi > CN. Mirrors
 * `regionWeight` in `src/admin/llm-models-routes.ts` for every region the
 * PROXY actually reports (server-side sort uses its own copy since it
 * operates on the raw proxy shape, not this file's `Region` type) — kept in
 * sync by inspection, not import, since the two run in different runtimes.
 * FRONT-1655 reuses it client-side to pick a model row's default provider
 * among several, so every member of `Region` has an explicit weight: `global`
 * is the one region besides EU that the Eddy offering admits
 * (EDDY_ALLOWED_REGIONS in src/llm/model-catalogue.ts), and used to fall
 * through to the `default` branch — ranked below CN.
 *
 * FRONT-1979: `de` is the one exception to "mirrors the backend exactly" —
 * it never reaches the server at all (see `deriveRegion`'s doc comment), so
 * the backend's own copy has no `de` case and doesn't need one.
 */
export function regionWeight(region: Region | undefined): number {
  switch (region) {
    case 'eu': return 0;
    case 'de': return 0;
    case 'global': return 1;
    case undefined: return 2;
    case 'us': return 2;
    case 'multi': return 3;
    case 'cn': return 4;
    default: return 9;
  }
}

/** The region as the picker labels it (EU / US / CN / Global / Multi), with `RegionBadge`'s own unknown-means-US fallback. */
export function regionLabel(region: Region | undefined): string {
  return REGION_LABEL[effectiveRegion(region)];
}

/**
 * The minimal row shape both pickers share — `ModelPicker`'s `ModelEntry`
 * (models-section.tsx) and `ModelCombobox`'s own (custom-agent-form.tsx)
 * both satisfy it structurally.
 */
export interface PickerModel {
  id: string;
  displayName?: string;
  variantLabel?: string;
  region?: Region;
  tags?: string[];
}

/**
 * FRONT-1655: the headline of a deduped picker row, and of the closed
 * trigger — one rule for both so they can't disagree. A genuine upstream
 * display name wins; otherwise the cloud/deployment-stripped `coreName`,
 * never the raw alias (a row may offer several providers, so
 * `bedrock-anthropic-claude-opus-5` as its title would bake one in).
 * `enrichModel` (src/llm/model-catalogue.ts) defaults `displayName` to the
 * raw id when the proxy has no real name, so `displayName ?? x` alone never
 * falls back — a genuine name is one that differs from the alias.
 */
export function modelHeadline(m: Pick<PickerModel, 'id' | 'displayName'>): string {
  return m.displayName && m.displayName !== m.id ? m.displayName : coreName(m.id);
}

/**
 * FRONT-1655: the cmdk search haystack for one deduped row — vendor, then
 * for every alias on the row: the alias, display name, variant label,
 * provider (serving cloud) name, region label and tags. Typing "stackit"
 * or "bedrock" surfaces every model that provider serves; typing "eu"
 * surfaces the EU-hosted ones (region used to be rendered as a flag but
 * never searchable — the gap FRONT-1655 recorded). With `regionKnownOnly`
 * an entry without a region contributes no region word, since an absent
 * region there means unknown, not US (FRONT-1034).
 */
export function modelSearchText(entries: readonly PickerModel[], regionKnownOnly?: boolean): string {
  const first = entries[0];
  return [
    first ? deriveFamily(first.id).label : '',
    ...entries.flatMap((e) => {
      const region = deriveRegion(e.id, e.region);
      return [
        e.id,
        e.displayName ?? '',
        e.variantLabel ?? '',
        deriveCloudProvider(e.id).label,
        region || !regionKnownOnly ? regionLabel(region) : '',
        ...(e.tags ?? []),
      ];
    }),
  ]
    .join(' ')
    .trim();
}

// A `Record<Region, boolean>`, not a hand-written disjunction — adding a
// future Region member forces this to be updated (compile error) the same
// way REGION_LABEL/REGION_CHIP_COLOR already do, rather than silently
// defaulting a new region to "no flag" the way an `||` chain would.
const REGION_HAS_FLAG: Record<Region, boolean> = {
  eu: true,
  us: true,
  cn: true,
  de: true,
  global: false,
  multi: false,
};

/** Regions with a real flag asset under `public/region-flags`; anything else gets a neutral text chip. */
export function hasRegionFlag(region: Region): boolean {
  return REGION_HAS_FLAG[region];
}

/**
 * The flag/text visual only, no `Tip` — the shared inner piece of
 * `RegionBadge` (standalone, 16px, as before FRONT-1655; chat-input.tsx's
 * image-mode line still uses it) and `CloudProviderBadge` (`compact`, 14px
 * inside the provider pill).
 */
function regionVisual(region: Region | undefined, compact = false): ReactElement {
  const r = effectiveRegion(region);
  if (hasRegionFlag(r)) {
    return (
      <img
        src={`/region-flags/${r}.webp`}
        alt={REGION_LABEL[r]}
        className={cn('inline-block shrink-0 rounded-sm object-cover', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')}
      />
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded text-[length:var(--text-2xs)] font-medium',
        compact ? 'px-1 leading-none' : 'px-1.5 py-0.5',
        REGION_CHIP_COLOR[r],
      )}
    >
      {REGION_LABEL[r]}
    </span>
  );
}

export function RegionBadge({ region }: { region?: Region }) {
  return <Tip label={REGION_LABEL[effectiveRegion(region)]}>{regionVisual(region)}</Tip>;
}

/**
 * FRONT-1655: one serving-cloud chip on a deduped model row — logo (or a
 * glyph fallback, same pattern as `ProviderBadge`) plus that alias's own
 * region flag, both inside ONE bordered pill with a SINGLE tooltip.
 *
 * Icon + flag share one border/background rather than each getting their
 * own — an early version gave the flag its own separate `RegionBadge`
 * (which wraps itself in its own `Tip`), and two providers on a row then
 * read as four separate hoverable circles instead of two chips, exactly
 * the "why does this look like 4 options" reading caught in review
 * (2026-08-28). One shared pill with one combined tooltip label
 * ("AWS Bedrock — EU") reads as one unit per provider, matching how a
 * single provider already read before this fix.
 *
 * No inline text label either: an even earlier version spelled the
 * provider name out inline, but with 2+ providers on a row that meant
 * either unreadable truncation ("AWS B…", "Google…") or, on a `global`/
 * `multi` region model, the wide "Global" text spilling past its own chip
 * because there was no room left to clip it into (`google-qwen3-next-80b-maas`,
 * same review pass). Icon + flag is a fixed, small width regardless of how
 * long the provider's name is, so neither failure mode has anywhere to
 * happen. `ProviderBadge` above already proved icon/glyph + Tip, no inline
 * text reads fine for vendor identity; this reuses it for provider identity.
 *
 * `onSelect` present + `interactive` true (the model has more than one
 * provider) makes the chip its own click target; `stopPropagation` keeps
 * that click from also firing the parent `CommandItem`'s row-level
 * `onSelect` (which would pick the row's DEFAULT provider instead of the
 * one actually clicked). A single-provider model passes no `onSelect` —
 * the chip is informational only, matching the "auto-skip step two, but
 * still show the info" decision in the design doc.
 *
 * One click each, two independent targets — the row (anywhere except a
 * chip) picks the default provider, a chip picks that exact one — no
 * forced sequence between them (2026-08-28, live review: an earlier
 * tap-to-arm/tap-to-confirm pass on the chip itself was over-engineering;
 * the actual ask was just "make the chip look pressable", not a second
 * gesture on the same small target). `interactive` gets a visible raised
 * look (border + shadow) precisely so it doesn't read as a flat,
 * inert badge the way the non-interactive/single-provider case does.
 *
 * There is deliberately no "unavailable"/greyed-out state: the models
 * endpoint carries no per-alias reachability signal (`labelsAvailable` is
 * list-level), so a chip had nothing real to key it off — see the design
 * doc's revisions. Add it when the endpoint grows the signal, not before.
 */
export function CloudProviderBadge({
  id,
  provider,
  region,
  regionKnownOnly,
  selected,
  primary,
  onSelect,
}: {
  /**
   * FRONT-1979: the alias, so the badge can apply `deriveRegion` itself
   * (a `-stackit` id always displays as Germany) instead of every call site
   * having to remember to wrap `region` — three of them didn't, on this
   * very ticket's first pass.
   */
  id: string;
  provider: CloudProviderMeta;
  region?: Region;
  regionKnownOnly?: boolean;
  /** Visually marks this as the model's currently-selected provider. */
  selected?: boolean;
  /**
   * FRONT-1655 (2026-08-30 review): on a multi-provider row, the provider a
   * plain ROW click resolves to. A row click used to make that choice with
   * no visible trace; the chip now says so (filled background + tooltip)
   * without adding a gesture — the user who only clicks models can see
   * what they got, the user who clicks chips is unaffected.
   */
  primary?: boolean;
  onSelect?: () => void;
}) {
  const { theme } = useTheme();
  const src = provider.logo ? (theme === 'dark' ? provider.logo.dark : provider.logo.light) : undefined;
  const interactive = Boolean(onSelect);
  const displayRegion = deriveRegion(id, region);
  const showRegion = Boolean(displayRegion) || !regionKnownOnly;
  const tipLabel =
    [provider.label, showRegion && REGION_LABEL[effectiveRegion(displayRegion)]].filter(Boolean).join(' — ') +
    (primary ? ' · used when you click the row' : '');

  // A direct-API alias whose vendor has no logo (e.g. the STT/realtime
  // catalogue's `whisper-1` / `tts-1`, vendor "Other") would otherwise get
  // a pill reading only "API" — the same unexplained-abbreviation problem
  // the "DI" glyph had. With nothing to identify, show just the region (as
  // `RegionBadge` did before FRONT-1655), or nothing at all.
  if (provider.key === 'direct' && !provider.logo && !interactive) {
    return showRegion ? <Tip label={tipLabel}>{regionVisual(displayRegion, true)}</Tip> : null;
  }

  const chip = (
    <span
      className={cn(
        'inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full border shrink-0',
        // primary: a quiet but legible "this one" — bg-muted alone vanished
        // against the hovered row's own muted background (live, 2026-08-30).
        selected
          ? 'border-primary bg-primary/10'
          : primary
            ? 'border-foreground/35 bg-muted'
            : 'border-border bg-background',
        interactive && 'shadow-sm hover:shadow hover:bg-muted/60 active:scale-95 transition',
      )}
    >
      {src ? (
        <img src={src} alt="" className="w-4 h-4 rounded-sm object-contain shrink-0" />
      ) : (
        <span
          className={cn(
            // h-4 matches the logo's height; the width grows so a two- or three-letter
            // glyph at the 11px floor stays inside its tint (FRONT-2665).
            'inline-flex items-center justify-center h-4 min-w-4 px-0.5 rounded-sm text-[length:var(--text-2xs)] font-semibold tracking-tight shrink-0',
            provider.color,
          )}
        >
          {provider.glyph}
        </span>
      )}
      {showRegion && regionVisual(displayRegion, true)}
    </span>
  );

  if (!interactive) {
    return <Tip label={tipLabel}>{chip}</Tip>;
  }

  return (
    <Tip label={tipLabel}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onSelect?.();
        }}
        // cmdk's root onKeyDown owns Enter (preventDefault + select the
        // highlighted ROW), so a Tab-focused chip would otherwise commit
        // the row's default alias on Enter while Space picked the chip.
        // Stop the key here so Enter and Space both mean "this chip".
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            onSelect?.();
          }
        }}
        className="inline-flex cursor-pointer"
        aria-pressed={selected}
        aria-label={`Use ${provider.label}`}
      >
        {chip}
      </button>
    </Tip>
  );
}
